<script setup lang="ts">
const props = defineProps<{
  active: string
  sections?: string[]
}>()
const site = useSiteStore()
const emit = defineEmits<{ go: [string] }>()
const p = usePortfolioStore().profile
const fullName = computed(() => p?.fullname || p?.name || '')
const avatar = computed(() => resolveMediaUrl(p?.avatar))
// لوگوی برند کاربر؛ ممکن است رشته یا آبجکت رسانه باشد، پس از هلپر عبور می‌دهیم.
const logo = computed(() => resolveMediaUrl(p?.logo))
const firstName = computed(() => fullName.value.trim().split(/\s+/)[0] || '')
// آیکون‌های توپُر خمیری (fill سفید، گوشه‌گرد)
const items = [
  { id: 'hero', label: 'خانه', d: 'M12 3.2c.3 0 .5.1.7.3l7.8 6.8c.5.4.2 1.2-.5 1.2H19v8a1.5 1.5 0 01-1.5 1.5h-3a.5.5 0 01-.5-.5V16a2 2 0 10-4 0v4.5a.5.5 0 01-.5.5h-3A1.5 1.5 0 015 19.5v-8H4c-.7 0-1-.8-.5-1.2l7.8-6.8c.2-.2.4-.3.7-.3z', d2: '' },
  { id: 'services', label: 'خدمات', d: 'M9.5 3.5h5A1.5 1.5 0 0116 5v1.5h3.5A1.5 1.5 0 0121 8v10.5a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 18.5V8a1.5 1.5 0 011.5-1.5H8V5a1.5 1.5 0 011.5-1.5zm.5 3h4V5.5h-4v1z', d2: '' },
  { id: 'projects', label: 'پروژه‌ها', d: 'M3 6.5A1.5 1.5 0 014.5 5h4.6c.4 0 .8.2 1 .4L11.6 7h7.9A1.5 1.5 0 0121 8.5v10a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 18.5v-12z', d2: '' },
  { id: 'contact', label: 'تماس', d: 'M4 5.5h16A1.5 1.5 0 0121.5 7v.3L12 13.5 2.5 7.3V7A1.5 1.5 0 014 5.5z', d2: 'M2.5 9.6l8.9 5.8c.4.2.8.2 1.2 0l8.9-5.8v7.9a1.5 1.5 0 01-1.5 1.5h-16a1.5 1.5 0 01-1.5-1.5V9.6z' }
]
const visibleItems = computed(() =>
  Array.isArray(props.sections)
    ? items.filter(item => props.sections?.includes(item.id))
    : items
)

function selectSection(id: string) {
  emit('go', id)
  site.closeSideMenu()
}
</script>

<template>
  <!-- بک‌دراپ منوی موبایل -->
  <Teleport to="body">
    <div v-if="site.sideMenuOpen" class="fixed inset-0 z-[60] bg-[#2819504d] backdrop-blur-[2px] md:hidden"
         @click="site.closeSideMenu()" />
  </Teleport>
  <!-- سایدبار: در موبایل کشوی تمام‌ارتفاع، در دسکتاپ sticky با هم‌پوشانی روی پنل -->
  <aside class="fixed start-0 top-0 z-[70] flex h-dvh w-[min(82vw,300px)] min-w-[190px] flex-col gap-1.5 overflow-y-auto
              rounded-[32px] p-4 px-3.5 pb-5 shadow-[var(--t-clay)] transition-transform duration-500 [background:var(--t-sidebar)]
              [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
              md:sticky md:top-7 md:z-[2] md:-me-[34px] md:h-[calc(100dvh-56px)] md:max-h-[860px] md:w-[205px] md:max-w-[225px] md:flex-none md:translate-x-0 md:transition-none"
         :class="site.sideMenuOpen ? 'translate-x-0 shadow-2xl' : 'translate-x-[105%]'">
    <div class="mx-auto h-[104px] w-[104px] shrink-0 overflow-hidden rounded-full border-[6px] border-white/70
                shadow-[0_12px_22px_rgb(82_55_151/0.24),inset_0_2px_3px_rgb(255_255_255/0.8)]">
      <img v-if="avatar" :src="avatar" :alt="fullName" class="h-full w-full object-cover" />
    </div>
    <p class="mb-3 mt-3 flex items-center justify-center gap-1.5 text-center text-[16px] font-extrabold text-[var(--t-side-ink4)]">سلام، من {{ firstName }}هستم!
      <svg viewBox="0 0 24 24" class="h-[18px] w-[18px] shrink-0 drop-shadow-[0_2.5px_2px_rgb(70_40_140/0.35)]" fill="#ffd9a0"><path d="M7.5 11.5V5.8a1.3 1.3 0 012.6 0v4.4h.8V3.8a1.3 1.3 0 012.6 0v6.4h.8V5a1.3 1.3 0 012.6 0v7.8h.8V8.2a1.3 1.3 0 012.6 0v7.3c0 3.6-2.9 6.5-6.5 6.5s-6.5-2.9-6.5-6.5v-1l-2.2-3.8a1.2 1.2 0 012-1.2l.4.6z" /></svg>
    </p>
    <button v-for="it in visibleItems" :key="it.id" @click="selectSection(it.id)"
            class="flex items-center gap-3 rounded-[18px] px-4 py-3 text-right text-[13px] font-extrabold transition duration-300"
            :class="active === it.id
              ? 'scale-[1.04] bg-[var(--t-side-active-pill4)] text-[var(--t-side-active-ink4)] shadow-[var(--t-side-active-shadow4)]'
              : 'text-[var(--t-side-ink4)] hover:bg-white/25'">
      <svg v-if="!(it.id === 'hero' && logo)" viewBox="0 0 24 24" class="h-[19px] w-[19px] drop-shadow-[0_2.5px_2px_rgb(70_40_140/0.35)]" fill="currentColor">
        <path :d="it.d" /><path v-if="it.d2" :d="it.d2" />
      </svg>
      <img v-else :src="logo" :alt="fullName ? `لوگوی ${fullName}` : 'لوگو'"
           class="h-[22px] w-[22px] shrink-0 rounded-md object-contain drop-shadow-[0_2.5px_2px_rgb(70_40_140/0.35)]" />
      {{ it.label }}
    </button>
    <div class="mb-1.5 mt-auto rounded-[24px] p-4 text-center [background:var(--t-resume4)]
                shadow-[0_10px_24px_rgb(90_60_160/0.28),inset_0_2px_3px_rgb(255_255_255/0.95)]">
      <span class="inline-grid h-[46px] w-[46px] place-content-center rounded-2xl bg-gradient-to-b from-[#f8d888] to-[#f0bf55]
                   shadow-[inset_0_3px_4px_rgb(255_255_255/0.6),inset_0_-4px_6px_rgb(180_120_30/0.35),0_7px_14px_rgb(180_120_30/0.3)]
                   [animation:floatY_5s_ease-in-out_infinite]">
        <svg viewBox="0 0 24 24" class="h-6 w-6 drop-shadow-[0_1.5px_1px_rgb(180_120_30/0.4)]" fill="#fff"><path d="M5 15.5l-1.8-8c-.1-.6.5-1 1-.7L8.7 10l2.7-5.4c.2-.5.9-.5 1.2 0L15.3 10l4.5-3.2c.5-.3 1.1.1 1 .7l-1.8 8H5zM5 17.5h14v1a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 015 18.5v-1z" /></svg>
      </span>
      <p class="mb-1 mt-2 text-[13.5px] font-extrabold text-[var(--t-resume-ink4)]">رزومه‌ی کامل من</p>
      <p class="mb-3 text-[11.5px] text-[var(--t-badge4)]">سوابق، تحصیلات و جزئیات بیشتر</p>
      <a v-if="p.cv" :href="p.cv.url" target="_blank" rel="noopener noreferrer" class="clay-btn-pink inline-block">دانلود رزومه</a>
    </div>
  </aside>
</template>
